import React, {
    useContext,
    useRef,
} from 'react';

import PropTypes from 'prop-types';
import Swiper from 'react-id-swiper';
import SwiperCore, {
    Navigation,
    Pagination,
} from 'swiper';

import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { OfferPopup } from '../../../../components/OfferPopup';
import { CoreContext } from '../../../../providers';
import { media } from '../../../../services/media';
import { themeStyles } from '../../../../services/themeStyles';
import { BankOfferSlide } from '../BankOfferSlide';
import { SwipeForMore } from '../SwipeForMore';

const propTypes = {
    headline: PropTypes.string,
    scoreFactors: PropTypes.array,
    swipeInfo: PropTypes.bool,
    variant: PropTypes.oneOf(['green', 'orange']),
};

export const ScoreFactorsSlider = ({
    headline,
    scoreFactors,
    swipeInfo = false,
    variant = 'green',
}: any) => {
    const { openPopup } = useContext(CoreContext);
    const swiperRef = useRef(null);

    SwiperCore.use([Pagination, Navigation]);


    /** @type {import('react-id-swiper').ReactIdSwiperProps} */
    const params = {
        spaceBetween: 15,
        centeredSlides: true,
        slideClass: 'swiper-slide',
        loop: true,
        loopAdditionalSlides: 2,
        slidesPerView: 1,
        // preventClicks: true,
        pagination: {
            el: '.swiper-pagination',
            clickable: true,
        },
        navigation: {
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
        },
        breakpoints: {
            767: {
                spaceBetween: 90,
            },
            1199: {
                spaceBetween: 150,
            },
        },
    };

    return (
        <Slider variant={variant}>
            <Headline>{headline}</Headline>

            {scoreFactors.length && (
                <Swiper ref={swiperRef} {...params}>
                    {scoreFactors.map((item: any, index: number) => (
                        <BankOfferSlide
                            key={`${index}-slideContent`}
                            variant={variant}
                            onClick={() =>
                                openPopup(<OfferPopup {...item.offer} />)
                            }
                            item={item}
                            bankOffer={item.offer ? item.offer : {}}
                            width={1}
                        />
                    ))}
                </Swiper>
            )}
            {swipeInfo && <SwipeForMore />}
        </Slider>
    );
};

ScoreFactorsSlider.propTypes = propTypes;

const SwiperContainer = css`
    .swiper-container {
        overflow: unset;
        position: relative;

        ${media.min('md')} {
            max-width: calc(85% - 150px);
            margin: 0 auto;
        }

        ${media.min('xl')} {
            max-width: calc(60% - 150px);
        }
    }
`;

const SwiperSlide = css`
    .swiper-slide {
        opacity: 1;
        transition: opacity 0.2s;
        height: auto;
        max-width: 100% !important;

        &-prev {
            opacity: 0;

            ${media.min('md')} {
                opacity: 1;
            }
        }
    }
`;

const SwiperPagination = css`
    .swiper-pagination {
        margin-top: 15px;
        margin-bottom: 15px;
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100% !important;

        ${media.min('md')} {
            margin-top: 20px;
            margin-bottom: 20px;
        }

        ${media.min('xl')} {
            margin-top: 30px;
            margin-bottom: 30px;
        }
    }
`;

const SwiperButtonNext = css`
    .swiper-button-next {
        transform: translate(0, -50%) rotate(180deg);
        left: calc(100% + 15px);

        ${media.min('xl')} {
            left: calc(100% + 30px);
        }
    }
`;

const SwiperButtonPrev = css`
    .swiper-button-prev {
        right: calc(100% + 15px);

        ${media.min('xl')} {
            right: calc(100% + 30px);
        }
    }
`;

const SwiperButtons = css`
    .swiper-button-next,
    .swiper-button-prev {
        background-image: url('assets/images/slider-arrow.svg');
        width: 60px;
        height: 60px;
        background-size: cover;
        position: absolute;
        top: 50%;
        transform: translate(0, -50%);
        z-index: 0;
        cursor: pointer;

        display: none;
        opacity: 0.1;

        ${media.min('md')} {
            display: block;
        }

        ${media.min('xl')} {
            width: 100px;
            height: 100px;
        }

        &.swiper-button-disabled {
            display: none;
        }
    }

    .swiper-button-next {
        transform: translate(0, -50%) rotate(180deg);
    }
`;

const BulletUpscore = themeStyles(
    'upscore',
    css`
        .swiper-pagination-bullet {
            background-color: #f1eff0;
            background-image: linear-gradient(to top, #15d8e2 0%, #54ef93 100%);

            &-active {
                &:before {
                    background-image: linear-gradient(
                        to top,
                        #15d8e2 0%,
                        #54ef93 100%
                    );
                }
            }
        }
    `,
);

const BulletPenge = themeStyles(
    'pengestyrke',
    css`
        .swiper-pagination-bullet {
            background-color: #133143;

            &-active {
                &:before {
                    background-image: linear-gradient(
                        to top,
                        #133143 0%,
                        #133143 100%
                    );
                }
            }
        }
    `,
);

const SwiperBullet = css`
    .swiper-pagination-bullet {
        width: 6px;
        max-width: 6px;
        height: 6px;
        flex: 1 0 6px;
        display: block;
        border-radius: 6px;
        margin-right: 9px;
        cursor: pointer;

        ${media.min('lg')} {
            width: 8px;
            height: 8px;
            max-width: 8px;
            border-radius: 18px;
        }

        &-active {
            position: relative;

            &:before {
                position: absolute;
                left: -3px;
                top: -3px;
                right: -3px;
                bottom: -3px;
                content: '';
                border-radius: 12px;

                ${media.min('lg')} {
                    border-radius: 18px;
                    left: -4px;
                    top: -4px;
                    right: -4px;
                    bottom: -4px;
                }
            }

            &:after {
                position: absolute;
                left: -1px;
                top: -1px;
                right: -1px;
                bottom: -1px;
                content: '';
                border-radius: 12px;
                border: 1px solid white;

                ${media.min('lg')} {
                    border-radius: 18px;
                    left: -2px;
                    top: -2px;
                    right: -2px;
                    bottom: -2px;
                    border-width: 2px;
                }
            }
        }

        &:last-of-type {
            margin-right: 0;
        }
    }
`;

const SliderOrange = css`
    .swiper-pagination-bullet,
    .swiper-pagination-bullet:before {
        background-image: linear-gradient(to top, #e0565f 0%, #f2af64 100%);
    }
`;

const SliderUpscore = themeStyles(
    'upscore',
    css`
        ${media.max('md')} {
            margin-bottom: -1.6vw;
        }

        ${media.min('md')} {
            margin-bottom: -0.9vw;
        }
    `,
);

const SliderPengestyrke = themeStyles(
    'pengestyrke',
    css`
        max-width: 1200px;
        margin: 20px auto;
        padding: 0 15px;

        ${media.min('md')} {
            margin: 40px auto;
        }
    `,
);

export const Slider = styled.section<{ variant: 'green' | 'orange' }>`
    margin: 0 0 30px;

    ${SwiperContainer}
    ${SwiperSlide}
    ${SwiperPagination}
    ${SwiperButtonNext}
    ${SwiperButtonPrev}
    ${SwiperButtons}
    ${SwiperBullet}
    ${BulletUpscore}
    ${BulletPenge}

    ${SliderUpscore}
    ${SliderPengestyrke}
    ${(props) => props.variant === 'orange' && SliderOrange}
`;

export const Headline = styled.h3`
    text-align: center;
    font-size: 16px;
    font-weight: 300;
    line-height: 20px;
    margin-bottom: 15px;

    ${media.min('md')} {
        font-size: 20px;
        line-height: 25px;
        margin-bottom: 30px;
    }

    ${media.min('xl')} {
        font-size: 25px;
        line-height: 30px;
        margin-bottom: 35px;
    }
`;
